import { Bot } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

/** Per-task Super Agent gate override. Null inherits the project's default gate. */
export type AiGateValue = 'plan_first' | 'direct' | null

// The sentinel the Select uses for "inherit project default" (Radix Select can't use "").
const INHERIT = '__inherit__'

const OPTIONS: Array<{ value: string; label: string }> = [
  { value: INHERIT, label: 'Project default' },
  { value: 'plan_first', label: 'Plan first' },
  { value: 'direct', label: 'Direct' },
]

type Props = {
  value: AiGateValue
  onChange: (value: AiGateValue) => void
  layout?: 'sidebar' | 'horizontal'
  disabled?: boolean
}

/**
 * Compact selector for a task's Super Agent gate. Shown in task details so a task can
 * override its project's default gate (plan-review vs direct). Independent of the
 * project's target (code PR vs docs/content).
 */
export function AiGateField({ value, onChange, layout = 'horizontal', disabled }: Props) {
  const selectValue = value ?? INHERIT
  const handleChange = (v: string) => onChange(v === INHERIT ? null : (v as AiGateValue))
  const isSidebar = layout === 'sidebar'

  return (
    <Select value={selectValue} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger
        aria-label="AI gate"
        className={cn(
          isSidebar
            ? // Match the sibling sidebar rows (date/priority/assignee): a borderless,
              // full-width ghost button rather than a bolted-on bordered dropdown.
              'h-auto w-full justify-start gap-2 rounded border-0 bg-transparent px-0 py-1 text-sm text-sidebar-foreground shadow-none hover:bg-sidebar-accent focus-visible:ring-0'
            : 'h-7 w-auto gap-1.5 border-dashed text-xs',
        )}
      >
        <Bot
          className={cn(
            'size-3.5',
            isSidebar ? 'text-sidebar-foreground/60' : 'text-muted-foreground',
          )}
        />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
